import React, { useMemo, useRef, useState } from 'react'
import { jsPDF } from 'jspdf'
import { Button, Divider, Flex, Typography } from 'antd'
import { ChecklistDescriptions } from '@/pages/checklist/components/ChecklistDescriptions.tsx'
import { ChecklistGroupItemSection } from '@/pages/checklist/components/ChecklistGroupItem.tsx'
import { Checklist } from '@/common/types/checklist.def.ts'
import constate from 'constate'
import { stringArrayToSelectOptions } from '@/utils/misc.ts'
import { uniq } from 'lodash-es'
import { CustomCheckbox } from '@/common/components/AntdForm/AntdFormCheckbox.tsx'
import html2canvas from 'html2canvas'
import { EditChecklistForm } from '@/pages/checklist/components/EditChecklistForm.tsx'

type ChecklistDetailsProps = {}

export const ChecklistDetails: React.FC<ChecklistDetailsProps> = () => {
  const { data, visibleAssignees, filteredAssignees, setFilteredAssignees } = useChecklistDetails()
  const targetRef = useRef(null)

  const handleDownloadPDF = async () => {
    if (!data || !targetRef.current) return
    const canvas = await html2canvas(targetRef.current)
    const imgData = canvas.toDataURL('image/png')

    const margin = 10
    const pdf = new jsPDF('p', 'mm', 'a4')
    const pageWidth = pdf.internal.pageSize.getWidth() - margin * 2
    const pageHeight = pdf.internal.pageSize.getHeight() - margin * 2
    const imgWidth = pageWidth
    const imgHeight = (canvas.height * imgWidth) / canvas.width

    let y = 0
    while (y < imgHeight) {
      if (y > 0) pdf.addPage()
      pdf.addImage(imgData, 'PNG', margin, margin - y, imgWidth, imgHeight)
      y += pageHeight
    }
    pdf.save(`${data.employeeName}_${data.category}_checklist.pdf`)
  }

  if (!data) return null

  return (
    <Flex gap="middle" vertical ref={targetRef} style={{ margin: 16 }}>
      <Typography.Title level={2} style={{ margin: 0 }}>
        #{data.id} {data.employeeName} - {data.category}
      </Typography.Title>
      <Flex gap="middle" justify="end">
        <Button onClick={handleDownloadPDF}>Download PDF</Button>
        <EditChecklistForm key={`${data.id}_${data.updatedAt}`} checklist={data} />
      </Flex>
      <ChecklistDescriptions data={data} />
      <Divider />
      {visibleAssignees.length > 1 ?
        <Flex gap="small" vertical>
          <Typography.Text>Assignees</Typography.Text>
          <CustomCheckbox
            options={stringArrayToSelectOptions(visibleAssignees)}
            value={filteredAssignees}
            onChange={x => setFilteredAssignees(x as string[])}
          />
        </Flex>
        : null}
      {data.checklistGroupItems.map((group) => (
        <ChecklistGroupItemSection checklist={data} group={group} key={group.groupName} />
      ))}
    </Flex>
  )
}

export const [ChecklistDetailsProvider, useChecklistDetails] = constate(({ data }: {
  data: Checklist,
}) => {
  const visibleAssignees = useMemo(
    () => uniq(data.checklistGroupItems.map(x => x.items.map(i => i.assignee).flat()).flat()),
    [data],
  )
  const [filteredAssignees, setFilteredAssignees] = useState(visibleAssignees)

  return {
    data,
    visibleAssignees,
    filteredAssignees,
    setFilteredAssignees,
  }
})